// sim/evolutionBox.ts — the Evolution-Box loot/economy layer.
//
// An ADDITIVE layer that sits ON TOP of the existing gene-loot system (genes.ts +
// geneForge.ts) without rewriting any of it. There is no human player: each
// COLONY (the Brood) is the autonomous economic agent. A colony:
//   1) EARNS mutation energy — from kills (the MutationEnergySink seam, fed by
//      castes.ts) and from exploration income (energy per live Flyer, on cadence);
//   2) OPENS tiered loot boxes when it can afford one — each open spends energy
//      and FORGES genes through awardGeneFromEvent (the existing forge), with a
//      separate rare-bonus roll + a pity guarantee so progress never stalls;
//   3) ADVANCES tiers — opens at a tier accumulate; past a threshold the colony
//      graduates Common → Apex → Miracle (Miracle is terminal);
//   4) SACRIFICES duplicates — on cadence, extra copies of a (type,rarity) group
//      beyond keepPerGroup are retired (genes.ts retire) back into energy.
//
// DATA-ORIENTED + DETERMINISTIC + ALLOCATION-FREE (DESIGN.md §2, EFFICIENCY.md §B):
//   Per-colony state is a handful of Int32Array columns indexed by colony id —
//   no per-colony objects. All randomness comes from the injected seeded Rng
//   (never Math.random); colonies are processed in fixed id order. The update
//   loop reuses a single fixed scratch buffer and allocates nothing. Same seed +
//   same tick ⇒ identical state; bulk step N == N × single steps.
//
// SEAMS (zero-dep): MutationEnergySink (types.ts — kill energy in) and the
// ColonyEnergySource below (exploration income out). Neither couples the box
// system to the colony AI; it runs WITH or WITHOUT colonies wired.

import {
  BOX_TIER_COUNT,
  GENE_TYPE_COUNT,
  RARITY_COUNT,
  NIL,
} from '../types.ts';
import type {
  System,
  Tick,
  ColonyId,
  MutationEnergySink,
  EvolutionBoxConfig,
} from '../types.ts';
import type { Rng } from '../rng.ts';
import type { GeneInventory, GeneId } from './genes.ts';
import { awardGeneFromEvent } from './geneForge.ts';

/**
 * Read-only seam the box system uses to size exploration income: how many
 * colonies exist and how many live Flyers each has. Lazily read (no cached
 * array) so it stays allocation-free and always reflects the live world. A world
 * with no colony AI can stub this; the box system never touches colonies directly.
 */
export interface ColonyEnergySource {
  /** Number of colonies (the box system iterates [0, colonyCount)). */
  colonyCount(): number;
  /** Live Flyer count for colony `cid` (drives exploration income). */
  flyerCountOf(cid: number): number;
}

/**
 * The Evolution-Box economy system. Runs once per tick (LAST in the colony loop,
 * after kills/AI have moved energy), implements MutationEnergySink so kills feed
 * it directly, and drives the earn → open → sacrifice loop deterministically.
 */
export class EvolutionBoxSystem implements System, MutationEnergySink {
  readonly name = 'evolution-box';

  private readonly inv: GeneInventory;
  private readonly rng: Rng;
  private readonly config: EvolutionBoxConfig;
  private readonly source: ColonyEnergySource;
  private readonly clock: { tick: number };
  readonly maxColonies: number;

  // --- Per-colony columns (compact SoA, index by ColonyId). ---
  /** Unspent mutation energy. */
  private readonly energy: Int32Array;
  /** Current box tier (BOX_TIER.*). */
  private readonly tier: Int32Array;
  /** Boxes opened AT the current tier (resets to 0 on a tier-up). */
  private readonly boxesAtTier: Int32Array;
  /** Lifetime boxes opened. */
  private readonly boxesOpened: Int32Array;
  /** Opens since the last rare (drives the pity guarantee). */
  private readonly opensSinceRare: Int32Array;
  /** Lifetime rares earned (guaranteed-floor count). */
  private readonly raresEarned: Int32Array;
  /** Lifetime energy earned (kills + exploration). */
  private readonly energyEarned: Int32Array;
  /** Lifetime duplicates sacrificed. */
  private readonly sacrificed: Int32Array;

  // --- Global counters (deterministic; tests + the hash read them). ---
  boxesOpenedTotal = 0;
  genesGrantedTotal = 0;
  raresGrantedTotal = 0;
  pityTriggersTotal = 0;
  sacrificedGenesTotal = 0;
  energyFromKillsTotal = 0;
  energyFromExploreTotal = 0;

  /**
   * Reused dup-detection scratch: a count per (type,rarity) cell, indexed
   * [type * RARITY_COUNT + rarity]. Cleared per colony inside sacrifice (small,
   * fixed size) so the walk allocates nothing.
   */
  private readonly dupScratch: Int32Array;

  constructor(
    inv: GeneInventory,
    rng: Rng,
    config: EvolutionBoxConfig,
    source: ColonyEnergySource,
    maxColonies: number,
    clock: { tick: number },
  ) {
    if (maxColonies <= 0) throw new Error('EvolutionBoxSystem maxColonies must be > 0');
    // Every tier's box cost must be > 0, or the spend loop (open-while-affordable)
    // would never terminate for a colony at that tier.
    for (let t = 0; t < BOX_TIER_COUNT; t++) {
      if ((config.boxCost[t] as number) <= 0) {
        throw new Error(`EvolutionBoxSystem boxCost[${t}] must be > 0`);
      }
    }
    this.inv = inv;
    this.rng = rng;
    this.config = config;
    this.source = source;
    this.maxColonies = maxColonies;
    this.clock = clock;

    this.energy = new Int32Array(maxColonies);
    this.tier = new Int32Array(maxColonies);
    this.boxesAtTier = new Int32Array(maxColonies);
    this.boxesOpened = new Int32Array(maxColonies);
    this.opensSinceRare = new Int32Array(maxColonies);
    this.raresEarned = new Int32Array(maxColonies);
    this.energyEarned = new Int32Array(maxColonies);
    this.sacrificed = new Int32Array(maxColonies);

    this.dupScratch = new Int32Array(GENE_TYPE_COUNT * RARITY_COUNT);
  }

  // --- Earning ---------------------------------------------------------------

  /**
   * MutationEnergySink: award kill energy to `colony` (the killer's colony id).
   * Negative ids (no colony) are ignored. Allocation-free. Called from
   * castes.ts/killOrganism via the ctx.mutation seam.
   */
  onKill(colony: ColonyId): void {
    if (colony < 0) return;
    const e = this.config.energyPerKill;
    this.energy[colony] = (this.energy[colony] as number) + e;
    this.energyEarned[colony] = (this.energyEarned[colony] as number) + e;
    this.energyFromKillsTotal += e;
  }

  /**
   * Exploration income: each colony earns exploreEnergyPerFlyer per live Flyer.
   * Run on the incomeInterval cadence from update. Fixed colony order, no alloc.
   */
  awardExplore(): void {
    const per = this.config.exploreEnergyPerFlyer;
    const n = this.source.colonyCount();
    for (let cid = 0; cid < n && cid < this.maxColonies; cid++) {
      const gain = per * this.source.flyerCountOf(cid);
      if (gain <= 0) continue;
      this.energy[cid] = (this.energy[cid] as number) + gain;
      this.energyEarned[cid] = (this.energyEarned[cid] as number) + gain;
      this.energyFromExploreTotal += gain;
    }
  }

  // --- Opening ---------------------------------------------------------------

  /**
   * Open one box for `colony` at its current tier. The CALLER must have confirmed
   * affordability (energy >= boxCost[tier]); this spends the cost, forges the tier's
   * guaranteed genes, rolls a rare bonus (with a pity guarantee), updates the
   * rare streak, and applies tier progression. Deterministic + allocation-free.
   */
  openBox(colony: ColonyId): void {
    const cfg = this.config;
    const tier = this.tier[colony] as number;

    // Spend the cost (affordability is the caller's responsibility).
    this.energy[colony] = (this.energy[colony] as number) - (cfg.boxCost[tier] as number);

    const tick = this.clock.tick;
    const floor = cfg.rareRarityFloor;
    let gotRare = false;

    // 1) Guaranteed genes for this tier (forged through the existing forge).
    const guaranteed = cfg.guaranteedGenes[tier] as number;
    const quality = cfg.openQualityBonus[tier] as number;
    for (let g = 0; g < guaranteed; g++) {
      const id = awardGeneFromEvent(this.inv, this.rng, colony, tick, quality);
      if (id === NIL) continue; // colony at gene capacity — count nothing.
      this.genesGrantedTotal++;
      if ((this.inv.rarityOf(id) as number) >= floor) gotRare = true;
    }

    // 2) Rare BONUS roll + pity guarantee. The roll ALWAYS consumes one draw so
    // the RNG stream is independent of whether pity fires (determinism-stable).
    const roll = this.rng.int(100) < (cfg.rareChancePct[tier] as number);
    const pity = (this.opensSinceRare[colony] as number) + 1 >= cfg.pityThreshold && !gotRare;
    if (roll || pity) {
      const bonusId = awardGeneFromEvent(this.inv, this.rng, colony, tick, cfg.rareQualityBonus);
      if (bonusId !== NIL) {
        this.genesGrantedTotal++;
        // Pity (or a sub-floor bonus) is floored UP to guarantee a rare.
        if (pity || (this.inv.rarityOf(bonusId) as number) < floor) {
          this.inv.promoteToRarity(bonusId, floor);
        }
        // The bonus now counts as a rare grant.
        this.raresGrantedTotal++;
        if (pity) this.pityTriggersTotal++;
        gotRare = true;
      }
    }

    // 3) Rare streak: reset on a rare, else advance the pity counter.
    if (gotRare) {
      this.opensSinceRare[colony] = 0;
      this.raresEarned[colony] = (this.raresEarned[colony] as number) + 1;
    } else {
      this.opensSinceRare[colony] = (this.opensSinceRare[colony] as number) + 1;
    }

    // 4) Open bookkeeping.
    this.boxesOpened[colony] = (this.boxesOpened[colony] as number) + 1;
    this.boxesAtTier[colony] = (this.boxesAtTier[colony] as number) + 1;
    this.boxesOpenedTotal++;

    // 5) Tier progression (always progress; Miracle is terminal).
    if (
      tier < BOX_TIER_COUNT - 1 &&
      (this.boxesAtTier[colony] as number) >= (cfg.tierUpAfter[tier] as number)
    ) {
      this.tier[colony] = tier + 1;
      this.boxesAtTier[colony] = 0;
    }
  }

  // --- Sacrifice -------------------------------------------------------------

  /**
   * Sacrifice `colony`'s duplicate genes: walk its list once, keep the first
   * keepPerGroup of each (type,rarity) group, and retire the rest — refunding
   * sacrificeYield[rarity] energy per retired gene. Allocation-free: the small
   * fixed dupScratch is zeroed once per colony. The list MUTATES during the walk
   * (retire clears a node's next link), so we capture `next` BEFORE retiring.
   */
  sacrifice(colony: ColonyId): void {
    const cfg = this.config;
    const keep = cfg.keepPerGroup;
    const scratch = this.dupScratch;
    scratch.fill(0);

    let id: GeneId = this.inv.firstOf(colony);
    while (id !== NIL) {
      // Capture next NOW — retire(id) clears id's next link.
      const nxt = this.inv.nextOf(id);
      const type = this.inv.typeOf(id) as number;
      const rarity = this.inv.rarityOf(id) as number;
      const cell = type * RARITY_COUNT + rarity;
      const seen = scratch[cell] as number;
      if (seen >= keep) {
        // A duplicate: retire it and refund energy by rarity.
        this.inv.retire(id);
        const yield_ = cfg.sacrificeYield[rarity] as number;
        this.energy[colony] = (this.energy[colony] as number) + yield_;
        this.energyEarned[colony] = (this.energyEarned[colony] as number) + yield_;
        this.sacrificed[colony] = (this.sacrificed[colony] as number) + 1;
        this.sacrificedGenesTotal++;
      } else {
        scratch[cell] = seen + 1;
      }
      id = nxt;
    }
  }

  // --- Per-tick driver -------------------------------------------------------

  /**
   * One economy tick, in fixed colony order:
   *   (a) sacrifice pass (cadence, if enabled) — frees duplicates back to energy;
   *   (b) exploration income (cadence) — energy per live Flyer;
   *   (c) spend: each colony opens boxes while it can afford its tier's cost.
   * Energy is bounded per tick (income + sacrifice are finite, opens only spend),
   * so the open while-loop always terminates. Deterministic + allocation-free.
   */
  update(tick: Tick): void {
    const cfg = this.config;

    // (a) Sacrifice duplicates on cadence (0 = disabled).
    if (cfg.sacrificeInterval > 0 && tick % cfg.sacrificeInterval === 0) {
      const n = this.source.colonyCount();
      for (let cid = 0; cid < n && cid < this.maxColonies; cid++) this.sacrifice(cid);
    }

    // (b) Exploration income on cadence.
    if (tick % cfg.incomeInterval === 0) this.awardExplore();

    // (c) Spend: open boxes while affordable, then progress (handled in openBox).
    const n = this.source.colonyCount();
    for (let cid = 0; cid < n && cid < this.maxColonies; cid++) {
      while ((this.energy[cid] as number) >= (cfg.boxCost[this.tier[cid] as number] as number)) {
        this.openBox(cid);
      }
    }
  }

  // --- Accessors (the hash + tests read these) ------------------------------

  energyAt(cid: ColonyId): number {
    return this.energy[cid] as number;
  }
  tierAt(cid: ColonyId): number {
    return this.tier[cid] as number;
  }
  boxesOpenedAt(cid: ColonyId): number {
    return this.boxesOpened[cid] as number;
  }
  boxesAtTierAt(cid: ColonyId): number {
    return this.boxesAtTier[cid] as number;
  }
  opensSinceRareAt(cid: ColonyId): number {
    return this.opensSinceRare[cid] as number;
  }
  raresEarnedAt(cid: ColonyId): number {
    return this.raresEarned[cid] as number;
  }
  energyEarnedAt(cid: ColonyId): number {
    return this.energyEarned[cid] as number;
  }
  sacrificedAt(cid: ColonyId): number {
    return this.sacrificed[cid] as number;
  }
}
